import { HISTORIC_SITES } from './historicSitesData.js';

// Keep the V1 key and t/q fields so existing discoveries survive the upgrade.
const KEY = 'historicSitesProgressV1';
const ONBOARD_KEY = 'historicSitesOnboardedV1';
const DAY = 86400000;
const siteById = new Map(HISTORIC_SITES.map(s => [s.id, s]));
const isRecord = v => v && typeof v === 'object' && !Array.isArray(v);

export function createProgress({ storage, now = Date.now } = {}) {
  function getStorage() { return storage ?? globalThis.localStorage; }
  let raw = {};
  try { raw = JSON.parse(getStorage()?.getItem(KEY) ?? '{}'); } catch { /* Memory fallback. */ }
  const sites = Object.create(null);
  const courses = Object.create(null);
  if (isRecord(raw?.sites)) for (const [id, entry] of Object.entries(raw.sites)) {
    if (!siteById.has(id) || !isRecord(entry)) continue;
    sites[id] = {
      ...entry,
      t: Number.isFinite(entry.t) ? entry.t : now(),
      q: entry.q === 0 || entry.q === 1 ? entry.q : null,
    };
  }
  if (isRecord(raw?.courses)) for (const [id, entry] of Object.entries(raw.courses)) {
    if (isRecord(entry)) courses[id] = { ...entry };
  }
  let count = Object.keys(sites).length;
  function save() {
    try { getStorage()?.setItem(KEY, JSON.stringify({ v: 1, sites, courses })); } catch { /* Memory fallback. */ }
  }
  const has = id => Object.hasOwn(sites, id);
  function canQuiz(id) { return has(id) && siteById.get(id)?.quiz && !siteById.get(id).solemn; }

  return {
    has,
    get size() { return count; },
    get(id) { return has(id) ? { ...sites[id] } : null; },
    mark(id) {
      if (has(id) || !siteById.has(id)) return false;
      sites[id] = { t: now(), q: null };
      count++;
      save();
      return true;
    },
    setQuiz(id, correct, { review = false } = {}) {
      if (!canQuiz(id)) return;
      const entry = sites[id];
      const time = now();
      entry.firstQ ??= entry.q ?? (correct ? 1 : 0);
      entry.q = correct ? 1 : 0;
      entry.attempts = (Number.isInteger(entry.attempts) ? entry.attempts : 0) + 1;
      entry.lastAnsweredAt = time;
      entry.streak = correct ? Math.min(4, Math.max(0, Number.isInteger(entry.streak) ? entry.streak : 0) + 1) : 0;
      entry.nextReviewAt = time + [1, 1, 3, 7, 14][entry.streak] * DAY;
      entry.reviewRequested = false;
      if (review) entry.reviewedAt = time;
      save();
    },
    requestReview(id, requested = true) {
      if (!canQuiz(id)) return;
      sites[id].reviewRequested = !!requested;
      save();
    },
    reviewQueue({ limit = 3 } = {}) {
      return HISTORIC_SITES.filter(s => {
        if (!canQuiz(s.id)) return false;
        const p = sites[s.id];
        return p.reviewRequested || p.q === 0 || (Number.isFinite(p.nextReviewAt) && p.nextReviewAt <= now());
      }).sort((a, b) => {
        const pa = sites[a.id], pb = sites[b.id];
        return Number(!!pb.reviewRequested) - Number(!!pa.reviewRequested)
          || (pa.lastAnsweredAt ?? pa.t) - (pb.lastAnsweredAt ?? pb.t);
      }).slice(0, limit);
    },
    recordObservation(id, correct) {
      if (!has(id) || siteById.get(id)?.solemn) return;
      sites[id].observation = { correct: !!correct, answeredAt: now() };
      save();
    },
    getCourse(id) { return courses[id] ? { ...courses[id] } : null; },
    saveCourse(id, value) { courses[id] = { ...value }; save(); },
    isOnboarded() {
      try { return !!getStorage()?.getItem(ONBOARD_KEY); } catch { return true; }
    },
    markOnboarded() {
      try { getStorage()?.setItem(ONBOARD_KEY, '1'); } catch { /* Memory fallback. */ }
    },
    reset() {
      for (const k of Object.keys(sites)) delete sites[k];
      for (const k of Object.keys(courses)) delete courses[k];
      count = 0;
      save();
    },
  };
}

export const progress = createProgress();
